import React from 'react';
import { Box, Paper, Typography, Chip, alpha, useTheme } from '@mui/material';
import { FilterList, Search, CalendarToday, Source as SourceIcon, Gavel } from '@mui/icons-material';

export type ActiveFilterIconType = 'search' | 'calendar' | 'source' | 'gavel' | 'default';

export interface ActiveFilterItem {
  key: string;
  label: string;
  onRemove: () => void;
  /** Tipo de ícone padrão ou elemento React customizado */
  icon?: ActiveFilterIconType | React.ReactNode;
  /** Variante do chip: outlined (padrão) ou filled */
  variant?: 'outlined' | 'filled';
  /** Cor do chip */
  color?: 'primary' | 'secondary' | 'default';
}

export interface ActiveFiltersBarProps {
  /** Lista de filtros ativos a exibir como chips */
  filters: ActiveFilterItem[];
  /** Callback ao clicar em "Limpar Todos" */
  onClearAll: () => void;
  /** Ocultar a barra quando não houver filtros (padrão: true) */
  hideWhenEmpty?: boolean;
  /** Animação de entrada (ex.: motion.div). Se não passar, renderiza sem animação */
  wrapperComponent?: React.ElementType;
  wrapperProps?: Record<string, unknown>;
}

const DefaultIcon = FilterList;

const iconMap: Record<ActiveFilterIconType, React.ReactNode> = {
  search: <Search sx={{ fontSize: 16 }} />,
  calendar: <CalendarToday sx={{ fontSize: 16 }} />,
  source: <SourceIcon sx={{ fontSize: 16 }} />,
  gavel: <Gavel sx={{ fontSize: 16 }} />,
  default: <DefaultIcon sx={{ fontSize: 16 }} />,
};

function getIcon(icon?: ActiveFilterIconType | React.ReactNode): React.ReactNode {
  if (icon === undefined) return iconMap.default;
  if (typeof icon === 'string' && icon in iconMap) return iconMap[icon as ActiveFilterIconType];
  if (React.isValidElement(icon)) return icon;
  return iconMap.default;
}

/**
 * Barra de filtros ativos no padrão do Dashboard: caixa com borda verde,
 * label "Filtros Ativos:", chips removíveis e botão "Limpar Todos".
 * Componente reutilizável para todas as telas com filtros de tabela.
 */
export function ActiveFiltersBar({
  filters,
  onClearAll,
  hideWhenEmpty = true,
  wrapperComponent: Wrapper,
  wrapperProps = {},
}: ActiveFiltersBarProps) {
  const theme = useTheme();

  if (hideWhenEmpty && filters.length === 0) return null;

  const content = (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        mb: 1.5,
        border: `1px solid ${theme.palette.primary.main}`,
        bgcolor: alpha(theme.palette.primary.main, 0.05),
      }}
    >
      <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center">
          <FilterList sx={{ fontSize: 20, color: theme.palette.primary.main, mr: 1 }} />
          <Typography variant="body2" fontWeight={600} color="primary">
            Filtros Ativos:
          </Typography>
        </Box>

        {filters.map((filter) => (
          <Chip
            key={filter.key}
            icon={getIcon(filter.icon) as React.ReactElement}
            label={filter.label}
            onDelete={filter.onRemove}
            color={filter.color ?? 'primary'}
            variant={filter.variant ?? 'outlined'}
            size="small"
          />
        ))}

        <Chip
          label="Limpar Todos"
          onClick={onClearAll}
          color="error"
          variant="outlined"
          size="small"
          sx={{ ml: 'auto' }}
        />
      </Box>
    </Paper>
  );

  if (Wrapper) {
    return (
      <Wrapper {...wrapperProps}>
        {content}
      </Wrapper>
    );
  }

  return content;
}
